import React, { useCallback, useEffect, useRef, useState } from 'react';

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const getShouldShowVoiceInput = () => {
  if (typeof window === 'undefined') return false;

  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  const isTabletOrSmaller = window.matchMedia?.('(max-width: 1024px)').matches;
  const hasTouch = navigator.maxTouchPoints > 0;

  return (hasCoarsePointer || hasTouch) && isTabletOrSmaller;
};

const joinSpeechText = (baseText, transcript) => {
  const cleanBase = baseText.trim();
  const cleanTranscript = transcript.trim();

  if (!cleanBase) return cleanTranscript;
  if (!cleanTranscript) return cleanBase;
  return `${cleanBase} ${cleanTranscript}`;
};

const VoiceTextButton = ({
  value,
  onChange,
  onBeforeStart,
  ariaLabel = 'Use voice input',
  isActive = true,
  positionClassName = 'right-2 top-1/2 -translate-y-1/2',
}) => {
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const handledPointerActivationRef = useRef(false);
  const [isSupported, setIsSupported] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    setIsSupported(Boolean(getSpeechRecognition()));
    setShouldShow(getShouldShowVoiceInput());
  }, []);

  const abortListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition.abort();
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  useEffect(() => abortListening, [abortListening]);

  useEffect(() => {
    if (!isActive) abortListening();
  }, [abortListening, isActive]);

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  const startListening = () => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition || !isActive) return;

    abortListening();
    onBeforeStart?.();

    const recognition = new SpeechRecognition();
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    baseTextRef.current = value || '';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join(' ');

      onChange(joinSpeechText(baseTextRef.current, transcript));
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported || !shouldShow) return null;

  return (
    <button
      type="button"
      className={`absolute z-30 ${positionClassName} flex h-8 w-8 touch-manipulation items-center justify-center rounded-full transition-colors ${
        isListening ? 'bg-[#152FF5] text-white' : 'bg-white text-[#3C5BD2]'
      }`}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse') return;

        event.preventDefault();
        event.stopPropagation();
        handledPointerActivationRef.current = true;
        toggleListening();
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();

        if (handledPointerActivationRef.current) {
          handledPointerActivationRef.current = false;
          return;
        }

        toggleListening();
      }}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <svg
        aria-hidden="true"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <path d="M12 19v3" />
      </svg>
    </button>
  );
};

export default VoiceTextButton;
